import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, Ban, CheckCircle2, ShieldCheck, Users as UsersIcon, Eye, UserPlus, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { api, apiError } from "../lib/api";

const SOURCE_LABEL = {
  registration: "Inscription",
  invitation: "Invitation",
  join_request: "Demande d'adhésion",
  securepass_admin: "Rattachement admin",
  legacy: "Historique",
};
const ROLE_OPTIONS = [
  { value: "member", label: "Utilisateur" },
  { value: "admin", label: "Administrateur de l'entreprise" },
  { value: "billing_admin", label: "Responsable facturation" },
];

export const AdminUsers = () => {
  const [users, setUsers] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attachWs, setAttachWs] = useState("");
  const [attachRole, setAttachRole] = useState("member");
  const [attaching, setAttaching] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [u, w] = await Promise.all([api.get("/admin/users"), api.get("/admin/workspaces")]);
      setUsers(u.data);
      setWorkspaces(w.data);
    } catch (e) {
      setLoadError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSuspend = async (id) => {
    try {
      const { data } = await api.post(`/admin/clients/${id}/suspend`);
      toast.success(data.suspended ? "Utilisateur suspendu" : "Utilisateur réactivé");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    setAttachWs("");
    setAttachRole("member");
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error("Impossible de charger les détails.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const attach = async () => {
    if (!attachWs) { toast.error("Sélectionnez un espace."); return; }
    setAttaching(true);
    try {
      await api.post(`/admin/workspaces/${attachWs}/attach-user`, { user_id: detail.id, role: attachRole });
      toast.success("Utilisateur rattaché à l'espace.");
      await openDetail(detail.id);
      await load();
      setAttachWs("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setAttaching(false);
    }
  };

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center" data-testid="admin-users-view">
        <p className="text-slate-500">Impossible de charger les utilisateurs.</p>
        <Button onClick={load} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">Réessayer</Button>
      </div>
    );
  }
  if (!users) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const memberWsIds = new Set((detail?.workspaces || []).map((w) => w.workspace_id));
  const attachable = workspaces.filter((w) => !memberWsIds.has(w.id));

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="space-y-6" data-testid="admin-users-view"
    >
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <UsersIcon className="w-5 h-5 text-emerald-600" />
          <h3 className="font-heading text-lg font-semibold">Utilisateurs ({users.length})</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold">Utilisateur</th>
                <th className="py-3 pr-4 font-semibold">Inscription</th>
                <th className="py-3 pr-4 font-semibold">Espaces</th>
                <th className="py-3 pr-4 font-semibold">Rôle</th>
                <th className="py-3 pr-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 pr-4">
                    <div className="font-medium text-slate-900 truncate flex items-center gap-1.5">
                      {u.name || "—"}
                      {u.two_factor_enabled && <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" title="2FA activée" />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    {u.suspended && (
                      <Badge variant="outline" className="mt-1 bg-red-500/15 text-red-600 border-red-500/30">Suspendu</Badge>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-slate-600 whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="py-3 pr-4 text-slate-600 font-mono-token">{u.workspace_count}</td>
                  <td className="py-3 pr-4">
                    {u.role === "admin"
                      ? <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Admin SecurePass</Badge>
                      : <span className="text-xs text-slate-500">Client</span>}
                  </td>
                  <td className="py-3 pr-2 text-right whitespace-nowrap">
                    <Button
                      data-testid={`user-detail-btn-${u.id}`}
                      onClick={() => openDetail(u.id)}
                      size="sm" variant="outline"
                      className="border-slate-200 bg-white hover:bg-slate-100 text-slate-900 mr-2"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {u.role !== "admin" && (
                      <Button
                        data-testid={`user-suspend-btn-${u.id}`}
                        onClick={() => toggleSuspend(u.id)}
                        size="sm" variant="outline"
                        className={u.suspended
                          ? "border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600"
                          : "border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-600"}
                      >
                        {u.suspended ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl bg-white max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-emerald-600" />
              {detail?.name || detail?.email || "Détails de l'utilisateur"}
            </DialogTitle>
            <DialogDescription>Espaces, rôles et rattachement manuel de cet utilisateur.</DialogDescription>
          </DialogHeader>
          {detailLoading || !detail?.workspaces ? (
            <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="E-mail" value={detail.email} />
                <Info label="Société" value={detail.company} />
                <Info label="2FA" value={detail.two_factor_enabled ? "Activée" : "Désactivée"} />
                <Info label="Statut" value={detail.suspended ? "Suspendu" : "Actif"} />
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> Espaces ({detail.workspaces.length})</h4>
                <div className="space-y-2" data-testid="user-detail-workspaces">
                  {detail.workspaces.length === 0 ? (
                    <p className="text-xs text-slate-400">Aucun espace.</p>
                  ) : detail.workspaces.map((w) => (
                    <div key={w.workspace_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm text-slate-900 truncate">{w.name}</div>
                        <div className="text-xs text-slate-500 font-mono-token">{w.public_ref}</div>
                      </div>
                      <div className="flex items-center flex-wrap gap-2 sm:shrink-0">
                        <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">{w.role_label}</Badge>
                        {w.source && (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/20 text-[10px]">
                            {SOURCE_LABEL[w.source] || w.source}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> Rattacher à un espace</h4>
                <div className="flex flex-col sm:flex-row gap-2" data-testid="attach-user-form">
                  <Select value={attachWs} onValueChange={setAttachWs}>
                    <SelectTrigger data-testid="attach-workspace-select" className="bg-slate-50 border-slate-200 flex-1">
                      <SelectValue placeholder="Choisir un espace" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {attachable.map((w) => (
                        <SelectItem key={w.id} value={w.id}>{w.name} · {w.public_ref}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={attachRole} onValueChange={setAttachRole}>
                    <SelectTrigger data-testid="attach-role-select" className="bg-slate-50 border-slate-200 w-full sm:w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="attach-user-btn"
                    onClick={attach}
                    disabled={attaching || !attachWs}
                    className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold shrink-0"
                  >
                    {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rattacher"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

const Info = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-xs text-slate-400 uppercase tracking-wide">{label}</div>
    <div className="text-slate-800 break-words">{value || "—"}</div>
  </div>
);
